/**
 * ImportExportTab Component
 *
 * Main container for CSV bulk import functionality.
 * Orchestrates the complete import flow from file upload to execution.
 *
 * Flow:
 * 1. Select import type (OrgUnits or Locations)
 * 2. Download CSV template
 * 3. Upload and parse CSV file
 * 4. Validate records
 * 5. Preview validation results
 * 6. Confirm and execute import
 * 7. Display results
 *
 * @module ImportExportTab
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminContext } from 'context/AdminContext';
import { useUser } from 'hooks/useUser';
import { API } from 'aws-amplify';
import { listOrgUnits, listLocations } from 'graphql/queries';
import AdminButton from 'components/common/AdminButton';
import TemplateDownloader from './components/TemplateDownloader';
import FileUploadDropzone from './components/FileUploadDropzone';
import ValidationPreview from './components/ValidationPreview';
import ImportSummary from './components/ImportSummary';
import ImportConfirmDialog from './components/ImportConfirmDialog';
import ImportResults from './components/ImportResults';
import useCSVParser from './hooks/useCSVParser';
import useCSVImport from './hooks/useCSVImport';

/**
 * ImportExportTab Component
 *
 * @param {Object} props
 * @param {Function} [props.onDataChange] - Optional callback when data is imported
 * @returns {JSX.Element}
 */
function ImportExportTab({ onDataChange }) {
  const queryClient = useQueryClient();
  const { selectedClient } = useAdminContext();
  const { user } = useUser();

  // Import type selection
  const [importType, setImportType] = useState('orgUnits');

  // Existing data from database
  const [existingOrgUnits, setExistingOrgUnits] = useState([]);
  const [existingLocations, setExistingLocations] = useState([]);
  const [loadingExistingData, setLoadingExistingData] = useState(false);

  // File parsing state
  const { parseState, parseFile, resetParse } = useCSVParser(existingOrgUnits, existingLocations);
  const { validationResult, error: parseError, loading: parsing } = parseState;

  // Import execution state
  const { importState, executeImport, resetImport } = useCSVImport(existingOrgUnits);
  const { loading: importing, results: importResults, error: importError } = importState;

  // UI state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  /**
   * Fetch existing OrgUnits and Locations for validation
   */
  useEffect(() => {
    const fetchExistingData = async () => {
      if (!selectedClient) return;

      setLoadingExistingData(true);
      try {
        // Fetch existing OrgUnits
        const orgUnitsResponse = await API.graphql({
          query: listOrgUnits,
          variables: {
            filter: { clientId: { eq: selectedClient } },
            limit: 10000
          }
        });
        const orgUnits = orgUnitsResponse.data.listOrgUnits.items || [];
        setExistingOrgUnits(orgUnits);

        // Fetch existing Locations
        const locationsResponse = await API.graphql({
          query: listLocations,
          variables: {
            filter: { clientId: { eq: selectedClient } },
            limit: 10000
          }
        });
        const locations = locationsResponse.data.listLocations.items || [];
        setExistingLocations(locations);
      } catch (error) {
        console.error('Error fetching existing data:', error);
        // Don't block the UI - validation will just not have existing data context
        setExistingOrgUnits([]);
        setExistingLocations([]);
      } finally {
        setLoadingExistingData(false);
      }
    };

    fetchExistingData();
  }, [selectedClient]);

  /**
   * Handle import type change
   */
  const handleImportTypeChange = (event) => {
    setImportType(event.target.value);
    resetParse(); // Clear any existing upload when changing type
    resetImport();
  };

  /**
   * Handle file upload
   */
  const handleFileUpload = async (file) => {
    resetImport(); // Clear any previous import results
    await parseFile(file, importType);
  };

  /**
   * Handle Import button click
   */
  const handleImportClick = () => {
    setConfirmDialogOpen(true);
  };

  /**
   * Handle import confirmation
   */
  const handleConfirmImport = async () => {
    await executeImport(
      importType,
      validationResult,
      selectedClient,
      user,
      {
        onSuccess: async () => {
          setConfirmDialogOpen(false);

          // Force immediate refetch of ALL LocationAdmin queries
          // Use refetchQueries instead of invalidateQueries for immediate update
          await Promise.all([
            queryClient.refetchQueries({
              queryKey: ['locationAdmin', selectedClient]
            }),
            queryClient.refetchQueries({
              queryKey: ['orgUnitsData', selectedClient]
            }),
            queryClient.refetchQueries({
              queryKey: ['hierarchyStructure', selectedClient]
            })
          ]);

          // Trigger parent data refresh callback
          if (onDataChange) {
            onDataChange();
          }

          // Re-fetch existing data to update cache for next import
          try {
            const orgUnitsResponse = await API.graphql({
              query: listOrgUnits,
              variables: {
                filter: { clientId: { eq: selectedClient } },
                limit: 10000
              }
            });
            const orgUnits = orgUnitsResponse.data.listOrgUnits.items || [];
            setExistingOrgUnits(orgUnits);

            const locationsResponse = await API.graphql({
              query: listLocations,
              variables: {
                filter: { clientId: { eq: selectedClient } },
                limit: 10000
              }
            });
            const locations = locationsResponse.data.listLocations.items || [];
            setExistingLocations(locations);
          } catch (error) {
            console.error('Error refreshing data after import:', error);
          }
        },
        onError: (error) => {
          console.error('Import failed:', error);
          setConfirmDialogOpen(false);
        }
      }
    );
  };

  /**
   * Handle cancel confirmation dialog
   */
  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
  };

  /**
   * Handle Start New Import
   */
  const handleStartNewImport = () => {
    setImportType('orgUnits');
    resetParse();
    resetImport();
  };

  // Helper text for import types
  const importTypeHelp = importType === 'orgUnits'
    ? 'Import organizational units (regions, districts, areas, etc.)'
    : 'Import store locations with addresses and settings';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h5" gutterBottom>
        CSV Bulk Import
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Import organizational units or locations from CSV files
      </Typography>

      {/* Step 1: Select Import Type */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 1: Select Import Type
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="import-type-label">Import Type</InputLabel>
            <Select
              labelId="import-type-label"
              id="import-type-select"
              value={importType}
              label="Import Type"
              onChange={handleImportTypeChange}
              disabled={!!importResults}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED',
                  borderWidth: 2
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6D28D9'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED'
                }
              }}
            >
              <MenuItem value="orgUnits">OrgUnits</MenuItem>
              <MenuItem value="locations">Locations</MenuItem>
            </Select>
            <FormHelperText>{importTypeHelp}</FormHelperText>
          </FormControl>
        </CardContent>
      </Card>

      {/* Step 2: Download Template */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Step 2: Download CSV Template
          </Typography>
          <TemplateDownloader importType={importType} />
        </CardContent>
      </Card>

      {/* Step 3: Upload CSV File */}
      {!importResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Step 3: Upload CSV File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              CSV format, UTF-8 encoding, max 10MB
            </Typography>

            <FileUploadDropzone
              onFileSelect={handleFileUpload}
              accept=".csv"
              maxSize={10485760}
              disabled={parsing || importing}
            />

            {parseError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {parseError}
              </Alert>
            )}

            {importError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Import failed: {importError}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Validation Results */}
      {validationResult && !importResults && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 4: Review Validation Results
              </Typography>

              {/* Import Summary */}
              <Box sx={{ mb: 3 }}>
                <ImportSummary validationResult={validationResult} />
              </Box>

              {/* Validation Preview Table */}
              <ValidationPreview
                validationResult={validationResult}
                importType={importType}
              />

              {/* Import Action */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <AdminButton
                  variant="outlined"
                  onClick={handleStartNewImport}
                  disabled={importing}
                  sx={{
                    borderColor: '#7C3AED',
                    color: '#7C3AED',
                    '&:hover': {
                      borderColor: '#6D28D9',
                      backgroundColor: 'rgba(124, 58, 237, 0.04)'
                    }
                  }}
                >
                  Cancel
                </AdminButton>
                <AdminButton
                  onClick={handleImportClick}
                  disabled={importing || validationResult.summary.validCount === 0}
                >
                  Import {validationResult.summary.validCount} {validationResult.summary.validCount === 1 ? 'Record' : 'Records'}
                </AdminButton>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Import Results */}
      {importResults && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              Import Results
            </Typography>

            <ImportResults
              results={importResults}
              importType={importType}
            />

            <Divider sx={{ my: 3 }} />

            {/* Success Message */}
            {importResults.summary.errorCount === 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>All records successfully imported!</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {importResults.summary.successCount} {importResults.summary.successCount === 1 ? 'record' : 'records'} created.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Import completed with some errors</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Successfully imported: {importResults.summary.successCount} records
                </Typography>
                <Typography variant="body2">
                  Failed: {importResults.summary.errorCount} records
                </Typography>
              </Alert>
            )}

            {/* Start New Import */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <AdminButton onClick={handleStartNewImport}>
                Start New Import
              </AdminButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      {validationResult && (
        <ImportConfirmDialog
          open={confirmDialogOpen}
          validationResult={validationResult}
          importType={importType}
          onCancel={handleCancelConfirm}
          onConfirm={handleConfirmImport}
          loading={importing}
        />
      )}
    </Box>
  );
}

ImportExportTab.propTypes = {
  onDataChange: PropTypes.func
};

export default ImportExportTab;
